<template>
  <ul
    v-if="loading"
    class="list-select flex flex-col gap-050 h-full"
    :class="{ horizontal, 'items-center': horizontal }"
  >
    <li
      v-for="index in skeletonCount"
      :key="index"
      class="list-item bg-neutral-white"
    >
      <LcSkeleton data-testid="skeleton" height="2rem" radius="small" />
    </li>
  </ul>

  <ul
    v-else
    class="list-select flex flex-col gap-050 h-full"
    :class="{ horizontal, 'items-center': horizontal }"
  >
    <li
      v-for="(
        { value, selected, label, href, hrefTitle, customProps }, index
      ) in options"
      :key="value"
    >
      <slot v-bind="options[index]">
        <Component
          :is="selected ? 'span' : customComponent"
          v-if="customComponent"
          v-bind="{
            to: href,
            hrefTitle,
            ...customProps,
          }"
          role="button"
          tabindex="0"
          @click="handleOptionClick(options[index])"
          @keydown.enter="handleOptionClick(options[index])"
        >
          <span
            class="item flex items-center justify-between py-100"
            :class="[
              {
                selected,
                discreet: variant === 'discreet',
              },
              horizontal ? 'px-100' : 'px-150',
            ]"
          >
            {{ label }}
            <LcIcon
              :class="[
                {
                  invisible: !selected,
                },
                [size === 'small' ? 'ml-100' : 'ml-200'],
              ]"
              :name="variant === 'discreet' ? 'checkmark-small' : 'checkmark'"
            />
          </span>
        </Component>

        <Component
          :is="selected ? 'span' : href ? NuxtLinkLocale : 'span'"
          v-else
          v-bind="{
            to: href,
            hrefTitle,
            ...customProps,
          }"
          class="item flex items-center justify-between py-100"
          :class="[
            {
              selected,
              discreet: variant === 'discreet',
            },
            horizontal ? 'px-100' : 'px-150',
          ]"
          role="button"
          tabindex="0"
          @click="handleOptionClick(options[index])"
          @keydown.enter="handleOptionClick(options[index])"
        >
          {{ label }}
          <LcIcon
            :class="[
              {
                invisible: !selected,
              },
              [size === 'small' ? 'ml-100' : 'ml-200'],
            ]"
            :name="variant === 'discreet' ? 'checkmark-small' : 'checkmark'"
          />
        </Component>
      </slot>
    </li>
  </ul>
</template>

<script setup lang="ts">
import { NuxtLinkLocale } from '#components'

export type ListSelectOption = {
  customProps?: Record<string, unknown>
  href?: string
  hrefTitle?: string
  label: string
  selected: boolean
  value: string
}

type ListSelectEmit = {
  (event: 'optionClick', value: ListSelectOption): void
}

type ListSelectProps = {
  customComponent?: Component
  horizontal?: boolean
  loading?: boolean
  multiple?: boolean
  size?: 'large' | 'small'
  skeletonCount?: number
  variant?: 'discreet'
}

const emit = defineEmits<ListSelectEmit>()

const {
  customComponent,
  multiple,
  skeletonCount = 5,
} = defineProps<ListSelectProps>()

const options = defineModel<ListSelectOption[]>({ required: true })

const handleOptionClick = (option: ListSelectOption) => {
  options.value = options.value.map((opt) => {
    let selected = opt === option ? !opt.selected : false

    if (multiple) {
      selected = opt === option ? !opt.selected : opt.selected
    }

    return {
      ...opt,
      selected,
    }
  })

  emit('optionClick', option)
}
</script>

<style lang="scss" scoped>
.list-select {
  .item {
    border-radius: token('size/radius/s');
    background-color: token('color/background/primary/primary');

    @include focus-state {
      border-radius: token('size/radius/s');
      box-shadow: 0 0 0 token('size/border/200')
        token('color/border/accent/focused/focused');
    }

    &.selected {
      background: token('color/background/accent/info/info/bold');
    }

    &.discreet {
      background: token('color/background/secondary/secondary');

      &.selected {
        background: token('color/background/secondary/secondary/bold');
      }
    }
  }

  .item:not(.selected) {
    @include hover-state {
      background: token('color/background/accent/info/info/extralight');
      cursor: pointer;
    }
  }

  .invisible {
    visibility: hidden;
  }

  &.horizontal {
    flex-flow: row wrap;
    gap: token('size/space/gap/100');
    overflow-y: visible;
  }
}
</style>
